import { getter } from '../../../CodesThatsGottaGoSomewhereElse';
import { ObjectManager } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { KeyInput } from '../../../platform/input/KeyInput';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Camera } from '../../canvas/Camera';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { defineToolWithCommonBehaviors } from '../../canvas/tooling/Definitions';
import { InteractionToolBehavior } from '../../canvas/tooling/InteractionToolBehavior';
import { KeyMappings } from '../../canvas/tooling/KeyMappings';
import { ToolBehavior, ToolSettings } from '../../canvas/tooling/ToolBehavior';
import { ToolOverlay } from '../../canvas/tooling/ToolOverlay';
import { GraffitiTarget } from '../../canvas/visualization/Graffiti';
import { resolve } from '../../Container';
import { EraserToolBehavior } from './EraserTool';
import { marketSetScaledSize } from './MarkerCommands';
import { Marking } from './Marking';

class MarkerToolBehavior extends ToolBehavior {

    @resolve(Camera) private camera!: Camera;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(ToolOverlay) private overlay!: ToolOverlay;

    private color: string = '#ff5722';
    private size: number = 11;
    private markingData: number[] = [];
    private latestAt: number = -100;
    private latest = null as Point | null;
    private previous = null as Point | null;
    private subject: Physical | null = null;

    public init(settings: ToolSettings) {
        this.size = settings.size || this.size;
        this.color = settings.color || this.color;
        this.burden(this.overlay.add(this.paintPreview));
    }

    public getKeyMappings = getter(KeyMappings.from(
        ['KEY_1', [marketSetScaledSize, [1]]],
        ['KEY_2', [marketSetScaledSize, [2]]],
        ['KEY_3', [marketSetScaledSize, [3]]],
        ['KEY_4', [marketSetScaledSize, [4]]],
        ['KEY_5', [marketSetScaledSize, [5]]],
        ['KEY_6', [marketSetScaledSize, [6]]],
        ['KEY_7', [marketSetScaledSize, [7]]],
        ['KEY_8', [marketSetScaledSize, [8]]],
        ['KEY_9', [marketSetScaledSize, [9]]],
        ['KEY_0', [marketSetScaledSize, [0]]],
    ));

    public getPreferredCursor(state: string, xi: MouseInput | KeyInput) {
        if (state === 'marking') {
            return 'draw';
        }
        if (state === 'idle') {
            const subject = xi.targetPhysical;
            if (subject?.is(GraffitiTarget)) {
                return 'draw';
            }
            else {
                return 'not-allowed';
            }
        }
    }

    public onMouseDown(state: string, mi: MouseInput): string | void {
        const { camera } = this;

        if (state !== 'idle' || mi.button !== MouseButtons.Main) {
            return;
        }

        const subject = mi.targetPhysical;
        if (!subject?.is(GraffitiTarget)) return;

        this.subject = subject;
        this.appendNext(camera.toModelPoint(mi.position), false);
        return 'marking';
    }

    public onMouseMove(state: string, mi: MouseInput): string | void {
        const { camera, subject } = this;

        if (state !== 'marking' || !subject.alive) {
            return;
        }

        const now = performance.now();
        const d = now - this.latestAt;
        if (d >= 15) {
            this.appendNext(camera.toModelPoint(mi.position), mi.modifiers.shift);
            this.latestAt = now;
        }

        this.overlay.repaint();
    }

    public onMouseUp(state: string, mi: MouseInput): string | void {
        const { camera, markingData, objects, subject } = this;

        if (state !== 'marking') {
            return;
        }

        if (subject?.alive) {

            this.appendNext(camera.toModelPoint(mi.position), false);

            objects.update(u => {
                const nr = u.create(Marking);
                nr.origin = 'center';
                nr.color = this.color;
                nr.size = this.size;
                nr.value = JSON.stringify(markingData);
                subject.add(nr);
            });
        }

        this.markingData = null;
        this.subject = null;
        this.previous = null;
        this.latest = null;
        this.overlay.repaint();

        return 'idle';
    }

    private appendNext(mpt: Point, correct: boolean) {

        const sb = this.subject.bounds(RelativeTo.Model);

        // If appending a point outside the subject bounds, project it to the nearest point inside
        if (!sb.contains(mpt)) {
            mpt = sb.project(mpt);
        }

        // Convert to model point to local point relative to center
        const rpt = this.subject.transform(RelativeTo.Model)
            .translate(this.subject.size().multiply(0.5))
            .inverse()
            .apply(mpt);

        // const { previous, latest } = this;
        // console.log(previous, latest, next.distanceTo(latest))yy

        // if (correct && previous && latest) {

        //     if (next.distanceTo(latest) <= 0) return;

        //     // Shift: lock the vector of the
        //     // const dist = latest.distanceTo(next);
        //     const plv = Point.vector(previous, latest);
        //     const lnv = Point.vector(latest, next);
        //     const cnv = lnv.project(plv);

        //     const corrected = latest.add(cnv);

        //     // const dist = next.subtract(latest).normalize() .project(vec).length();
        //     // if (dist <= 0) return;

        //     // const corrected = latest.add(vec.multiply(dist));
        //     this.markingData.push(corrected.x, corrected.y)
        //     this.previous = this.latest;
        //     this.latest = corrected;
        // }
        // else {

        // If no latest, we must be the first
        if (!this.markingData || !this.latest) {
            this.latest = rpt;
            this.previous = null;
            this.markingData = [this.latest.x, this.latest.y];
        }
        else {
            this.previous = this.latest;
            this.latest = rpt;
            this.markingData.push(rpt.x, rpt.y);
        }
    }

    private paintPreview = (gfx: UniversalSurfaceRenderer) => {
        if (!this.markingData) return;
        const data = (this.markingData ?? []).concat(this.latest?.toArray() ?? []);
        if (data.length < 4) return;
        const pathData = Marking.toStrokePath(data, this.size);
        if (!pathData) return;

        const mv = this.camera.modelViewMatrix; // Model to view
        const om = this.subject.transform(RelativeTo.Model).translate(this.subject.size().multiply(0.5)); // Origin to model

        gfx.setTransform(mv.multiply(om));
        gfx.fillStyle = this.color;
        gfx.fill(new Path2D(pathData));

        const clip = this.subject.path(RelativeTo.Model).transform(mv).toPath2D();
        gfx.resetTransform();
        gfx.globalCompositeOperation = 'destination-in';
        gfx.fillStyle = '#000';
        gfx.fill(clip);
    };
}

export const markerTool = defineToolWithCommonBehaviors({
    id: 'markerTool',
    label: 'Marker',
    category: 'collaboration',
    icon: 'deqo://icons/d2/draw',
    weight: 1070,
    shortcut: 'M',
    behaviors: [
        EraserToolBehavior(MouseButtons.Secondary),
        MarkerToolBehavior,
        InteractionToolBehavior,
    ]
});
